import { XMarkIcon } from '@heroicons/react/24/outline'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { MultiSelectDropdown } from '@/components/forms/MultiSelectDropdown'
import { TextInput } from '@/components/forms/TextInput'
import { DownloadIcon, SearchIcon } from '@/components/icons'
import { BillingCycleBadge } from '@/components/shared/BillingCycleBadge'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { NameWithIcon } from '@/components/ui/NameWithIcon'
import useAbortControllers from '@/hooks/AbortControllers'
import {
  getDealersBillingMetrics,
  getBillingPeriods,
  createBillingCycleExport,
  getBillingCycleExport,
} from '@/services/billing.service'
import { getFilterListOptions } from '@/services/dealers.service'
import { BillingFilterStoreContext } from '@/stores/BillingFilterStore'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { MONTH_NAMES, BILLING_CYCLE_STATE_OPTIONS, CYCLE_STATES } from '@/utils/constants'
import { formatCurrency } from '@/utils/formatters'
import { handlePagination, toast } from '@/utils/helpers'

import { BillingPeriodPicker } from './components/BillingPeriodPicker'

const DEALER_BASE_URL = '/dealers/?expand=group,billing_metrics,manufacturer'
const DEALER_GROUP_BASE_URL = '/dealers/dealer-groups/'
const MANUFACTURER_BASE_URL = '/dealers/manufacturers/'
const TEAM_BASE_URL = '/dealers/team/'

const DEFAULT_FILTERS = {
  dealerGroup: [],
  manufacturer: [],
  team: [],
  billingStatus: [],
  billingPeriod: [],
}

const renderBillingCycleBadge = (status) => <BillingCycleBadge status={status} />
const renderOutstandingCycles = (cycles) => {
  if (!cycles || cycles.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1">
      {cycles.map((cycle) => {
        const stateStyle = CYCLE_STATES[cycle.state]?.color || 'bg-amber-100 text-amber-800'
        return (
          <span
            key={`${cycle.year}-${cycle.month}-${cycle.state}`}
            title={cycle.state}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${stateStyle}`}
          >
            {MONTH_NAMES[cycle.month - 1]} {cycle.year}
          </span>
        )
      })}
    </div>
  )
}
const renderTeam = (row) => (
  <div className="mb-2">
    <NameWithIcon name={row.admin} label="A" ariaText="Admin" />
    <NameWithIcon name={row.assistantManager} label="AM" ariaText="Assistant Manager" />
    <NameWithIcon name={row.manager} label="M" ariaText="Manager" />
  </div>
)
const renderDealerDetails = (row) => (
  <div className="my-2">
    <Link
      to={`/dealer/${row.id}/billing`}
      className="text-sm font-medium leading-5 tracking-[0.25px] text-blue-800"
    >
      {row.name}
    </Link>
    <div className="mt-2 text-sm font-medium leading-5 tracking-[0.25px]">
      {row.group?.name || 'N/A'}
    </div>
    <div className="mt-2 text-sm font-medium leading-5 tracking-[0.25px]">
      {row.manufacturer?.name || 'N/A'}
    </div>
  </div>
)
const renderCurrency = (value) => <span className="text-sm">{formatCurrency(value, false)}</span>

const Dealers = ({ currentMonth = '', previousMonth = '', onFiltersChange = () => {} }) => {
  const navigate = useNavigate()

  // Contexts
  const billingFilterStore = useContext(BillingFilterStoreContext)
  const { isWpcExecutive, isWpcManager } = useContext(UserStoreContext)
  const { createNewTask } = useContext(TaskStoreContext)

  // State — init from store for persistence
  const [loading, setLoading] = useState(true)
  const [loadingExport, setLoadingExport] = useState(false)
  const [loadingFilters, setLoadingFilters] = useState(true)
  const [dealers, setDealers] = useState([])
  const [sortedColumn, setSortedColumn] = useState(billingFilterStore.sortedColumn)
  const [filters, setFilters] = useState({
    dealerGroup: [],
    manufacturer: [],
    team: [],
  })
  const [selectedFilters, setSelectedFilters] = useState(billingFilterStore.selectedFilters)
  const [billingPeriods, setBillingPeriods] = useState([])
  const [showExportModal, setShowExportModal] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState({})
  const [search, setSearch] = useState(billingFilterStore.searchTerm)
  const [searchTerm, setSearchTerm] = useState(billingFilterStore.searchTerm)

  // Refs
  const searchInputRef = useRef()
  const isBillingInitialMount = useRef(true)

  // Pagination — init from store
  const [currentPage, setCurrentPage] = useState(billingFilterStore.currentPage)
  const [totalRows, setTotalRows] = useState()
  const [perPage, setPerPage] = useState(billingFilterStore.perPage)
  const [pages, setPages] = useState(null)

  // Debounced search function
  const searchDealers = useCallback(_.debounce(setSearch, 500), [])

  // Sync state to store for persistence
  useEffect(() => {
    billingFilterStore.setSelectedFilters(selectedFilters)
  }, [selectedFilters])
  useEffect(() => {
    billingFilterStore.setSortedColumn(sortedColumn)
  }, [sortedColumn])
  useEffect(() => {
    billingFilterStore.setCurrentPage(currentPage)
  }, [currentPage])
  useEffect(() => {
    billingFilterStore.setPerPage(perPage)
  }, [perPage])
  useEffect(() => {
    billingFilterStore.setSearchTerm(search)
  }, [search])

  const handleErrors = (m) => toast(m, 'error')

  // Cancels superseded metrics/filter requests so the last filter/page
  // *requested* wins, not the last to *resolve* (filter/search/pagination races).
  const { getSignal } = useAbortControllers()

  const columns = useMemo(
    () => [
      {
        cell: (row) => renderDealerDetails(row),
        id: 'dealerName',
        width: '250px',
        name: 'Dealer Name',
        selector: (row) => row.name,
        sortable: true,
        sortBy: 'name',
      },
      {
        id: 'currentMonthTotal',
        name: <span className="whitespace-normal text-left">{currentMonth} Running Total</span>,
        format: (row) => renderCurrency(row.billingMetrics?.currentMonthTotal),
        selector: (row) => row.billingMetrics?.currentMonthTotal,
        sortable: true,
        sortBy: 'current_month_total',
      },
      {
        grow: 0.5,
        id: 'currentMonthSubmissionStatus',
        name: (
          <span className="whitespace-normal text-left">{currentMonth} Submission Status</span>
        ),
        format: (row) => renderBillingCycleBadge(row.billingMetrics?.currentMonthState),
        selector: (row) => row.billingMetrics?.currentMonthState,
        sortable: true,
        sortBy: 'current_month_state',
        width: '250px',
      },
      {
        id: 'previousMonthTotal',
        name: <span className="whitespace-normal text-left">{previousMonth} Total</span>,
        format: (row) => renderCurrency(row.billingMetrics?.previousMonthTotal),
        selector: (row) => row.billingMetrics?.previousMonthTotal,
        sortable: true,
        sortBy: 'previous_month_total',
      },
      {
        grow: 0.5,
        id: 'previousMonthSubmissionStatus',
        name: (
          <span className="whitespace-normal text-left">{previousMonth} Submission Status</span>
        ),
        format: (row) => renderBillingCycleBadge(row.billingMetrics?.previousMonthState),
        selector: (row) => row.billingMetrics?.previousMonthState,
        sortable: true,
        sortBy: 'previous_month_state',
        width: '250px',
      },
      {
        id: 'outstandingCycles',
        name: 'Outstanding Cycles',
        cell: (row) => renderOutstandingCycles(row.billingMetrics?.outstandingCycles),
        sortable: false,
        width: '250px',
      },
      {
        cell: (row) => renderTeam(row),
        id: 'admin',
        width: '250px',
        name: 'WPC Team',
        selector: (row) => row.admin,
        sortable: false,
      },
    ],
    [currentMonth, previousMonth],
  )

  /**
   * Gets the updated list of dealers; updates pagination.
   * @param {string} url
   */
  const getUpdatedDealersMetrics = async (url) => {
    const response = await getDealersBillingMetrics(
      url,
      setLoading,
      handleErrors,
      () => {},
      getSignal('dealer-metrics'),
    )

    if (response) {
      setDealers(response.results)
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
    }
    return response
  }

  /**
   * Configures the filter query, without the specified `without` value.
   * @param {string} without
   */
  const configureFilterQuery = (without = null) => {
    const filterQuery = {
      group: selectedFilters.dealerGroup,
      manufacturer: selectedFilters.manufacturer,
      team: selectedFilters.team,
    }

    if (without) delete filterQuery[without]

    return _.map(filterQuery, (value, key) => {
      if (value.length === 0) return null
      if (key === 'group') return `${key}=${_.map(value, 'id').join('&group=')}`
      return `${key}=${_.map(value, 'id').join(`&${key}=`)}`
    })
      .filter((x) => x !== null)
      .join('&')
  }

  /**
   * Builds the billing cycle filter query string from selectedFilters.
   */
  const buildBillingFilterString = () => {
    const billingFilters = []
    if (selectedFilters.billingStatus.length > 0) {
      selectedFilters.billingStatus.forEach((s) => {
        billingFilters.push(`billing_cycle__state=${encodeURIComponent(s.id)}`)
      })
    }
    if (selectedFilters.billingPeriod.length > 0) {
      selectedFilters.billingPeriod.forEach((p) => {
        billingFilters.push(`billing_cycle__period=${p.id}`)
      })
    }
    return billingFilters.join('&')
  }

  // Effect 1: Fetch filter list options (only depends on dealer filter selections)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setLoadingFilters(true)

      const configureFilterOption = (values, isObject = false) =>
        _.orderBy(
          _.map(values, (o) => (isObject ? { id: o.id, label: o.name } : { id: o, label: o })),
          'label',
        )

      const values = await Promise.all([
        getFilterListOptions(
          `${DEALER_GROUP_BASE_URL}?${configureFilterQuery('group')}`,
          () => {},
          () => {},
          getSignal('filter-group'),
        ),
        getFilterListOptions(
          `${MANUFACTURER_BASE_URL}?${configureFilterQuery('manufacturer')}`,
          () => {},
          () => {},
          getSignal('filter-manufacturer'),
        ),
        getFilterListOptions(
          `${TEAM_BASE_URL}?${configureFilterQuery('team')}`,
          () => {},
          () => {},
          getSignal('filter-team'),
        ),
      ])

      setFilters({
        dealerGroup: configureFilterOption(values[0], true),
        manufacturer: configureFilterOption(values[1], true),
        team: configureFilterOption(values[2]),
      })
      setLoadingFilters(false)
    }

    fetchFilterOptions()
  }, [selectedFilters.dealerGroup, selectedFilters.manufacturer, selectedFilters.team])

  // Effect 2: Fetch dealer data (depends on all filters, pagination, sorting, search)
  useEffect(() => {
    const getUpdatedData = async () => {
      setLoading(true)

      const dealerFilters = configureFilterQuery()
      const billingFilterString = buildBillingFilterString()

      // Build filter string for KPIs
      let kpiFilterString = ''

      if (dealerFilters && billingFilterString) {
        kpiFilterString = `${dealerFilters}&${billingFilterString}`
      } else if (dealerFilters) {
        kpiFilterString = dealerFilters
      } else if (billingFilterString) {
        kpiFilterString = billingFilterString
      }

      // Add search parameter if present
      if (search) {
        kpiFilterString = kpiFilterString ? `${kpiFilterString}&q=${search}` : `q=${search}`
      }

      // Notify parent component about filter changes for KPIs
      onFiltersChange(kpiFilterString)

      // Build URL with search parameter
      const page = isBillingInitialMount.current ? currentPage : 1
      if (!isBillingInitialMount.current) setCurrentPage(1)
      isBillingInitialMount.current = false
      let url = `${DEALER_BASE_URL}&limit=${perPage}&page=${page}&order_by=${sortedColumn}${
        billingFilterString ? `&${billingFilterString}` : ''
      }${dealerFilters ? `&${dealerFilters}` : ''}`

      // Add search parameter if present
      if (search) {
        url += `&q=${search}`
      }

      await getUpdatedDealersMetrics(url)
    }

    getUpdatedData()
  }, [
    selectedFilters,
    perPage,
    sortedColumn,
    currentMonth,
    previousMonth,
    onFiltersChange,
    search,
  ])

  /**
   * Gets the updated list of billing periods.
   */
  useEffect(() => {
    const fetchBillingPeriods = async () => {
      const data = await getBillingPeriods(setLoading, setLoading, handleErrors)
      if (data) {
        setBillingPeriods(data)
      }
    }

    fetchBillingPeriods()
  }, [])

  const handleExport = async () => {
    // Build filter query params to pass to the export endpoint
    const dealerFilters = configureFilterQuery()
    const billingFilterString = buildBillingFilterString()

    let filterParams = ''
    if (dealerFilters && billingFilterString) {
      filterParams = `${dealerFilters}&${billingFilterString}`
    } else if (dealerFilters) {
      filterParams = dealerFilters
    } else if (billingFilterString) {
      filterParams = billingFilterString
    }

    // Add search parameter if present
    if (search) {
      filterParams = filterParams ? `${filterParams}&q=${search}` : `q=${search}`
    }

    const exportTask = await createBillingCycleExport(
      selectedPeriod.id,
      filterParams,
      handleErrors,
      setLoadingExport,
    )

    if (exportTask) {
      // close the modal
      setShowExportModal(false)

      // create a new task
      createNewTask('Download', exportTask, {
        label: 'Billing Cycle',
        filename: `Billing Cycle Export - ${selectedPeriod.month}-${selectedPeriod.year}.xlsx`,
        checkUpdatedTask: async () => {
          const updatedTask = await getBillingCycleExport(
            selectedPeriod.id,
            exportTask.id,
            handleErrors,
          )
          return updatedTask
        },
      })

      // reset the selected period
      setSelectedPeriod({})
    }
  }

  return (
    <div className="mt-6">
      <div className="my-4 flex flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          <span className="text-charcoal-900 text-2xl font-semibold leading-8 tracking-[-1px]">
            Dealers
          </span>

          {(isWpcExecutive || isWpcManager) && (
            <Button
              className="p-2"
              dataTestId="export-billing-cycle"
              label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
              ariaLabel="Export Billing Cycle"
              loading={loadingExport}
              onClick={() => setShowExportModal(true)}
            />
          )}
        </div>

        <div className="my-4 flex flex-wrap items-center justify-between gap-4">
          <TextInput
            className="w-72 pl-10"
            data-testid="dealer-search"
            icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
            endIcon={
              searchTerm ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('')
                    searchDealers('')
                    searchInputRef.current.value = ''
                  }}
                >
                  <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                </button>
              ) : null
            }
            onChange={(e) => {
              searchDealers(e.target.value)
              setSearchTerm(e.target.value)
            }}
            placeholder="Search"
            value={searchTerm}
            ref={searchInputRef}
          />

          <div className="flex w-full flex-col flex-wrap items-center justify-end gap-2 md:flex-row lg:w-auto">
            {_.values(selectedFilters).some((o) => Array.isArray(o) && o.length > 0) && (
              <button
                className="mr-3 cursor-pointer rounded-md text-sm font-semibold leading-5 tracking-[0.25px] text-blue-800 hover:text-blue-900"
                onClick={() => {
                  setSelectedFilters(DEFAULT_FILTERS)
                }}
                type="button"
              >
                Reset Filters
              </button>
            )}

            <MultiSelectDropdown
              className="w-full md:max-w-[250px]"
              containerClassName="w-full md:w-auto"
              disabled={loadingFilters}
              id="billing-status-filter"
              onChange={(o) =>
                setSelectedFilters((prev) => ({
                  ...prev,
                  billingStatus: o,
                  ...(o.length === 0 ? { billingPeriod: [] } : {}),
                }))
              }
              options={BILLING_CYCLE_STATE_OPTIONS}
              placeholder="Select a Status"
              search
              value={selectedFilters.billingStatus}
            />

            {selectedFilters.billingStatus.length > 0 && (
              <MultiSelectDropdown
                className="w-full md:max-w-[250px]"
                containerClassName="w-full md:w-auto"
                disabled={loadingFilters}
                id="billing-period-filter"
                onChange={(o) => setSelectedFilters((prev) => ({ ...prev, billingPeriod: o }))}
                options={billingPeriods.map((p) => ({
                  id: p.id,
                  label: `${MONTH_NAMES[p.month - 1]} ${p.year}`,
                }))}
                placeholder="Select a Month"
                search
                value={selectedFilters.billingPeriod}
              />
            )}

            <MultiSelectDropdown
              className="w-full md:max-w-[250px]"
              containerClassName="w-full md:w-auto"
              disabled={loadingFilters}
              id="dealer-group-filter"
              onChange={(o) => setSelectedFilters((prev) => ({ ...prev, dealerGroup: o }))}
              options={filters.dealerGroup}
              placeholder="Select a Dealer Group"
              search
              value={selectedFilters.dealerGroup}
            />

            <MultiSelectDropdown
              className="w-full md:max-w-[250px]"
              containerClassName="w-full md:w-auto"
              disabled={loadingFilters}
              id="manufacturer-filter"
              onChange={(o) => setSelectedFilters((prev) => ({ ...prev, manufacturer: o }))}
              options={filters.manufacturer}
              placeholder="Select a Manufacturer"
              search
              value={selectedFilters.manufacturer}
            />

            <MultiSelectDropdown
              className="w-full md:max-w-[250px]"
              containerClassName="w-full md:w-auto"
              disabled={loadingFilters}
              id="team-filter"
              onChange={(o) => setSelectedFilters((prev) => ({ ...prev, team: o }))}
              options={filters.team}
              placeholder="Select a Team Member"
              search
              value={selectedFilters.team}
            />
          </div>
        </div>
      </div>

      {/* Billing Cycles DataTable */}
      <DataTable
        onRowClicked={(row) => navigate(`/dealer/${row.id}/billing`)}
        highlightOnHover
        columns={columns}
        data={dealers}
        {...getSortProps(sortedColumn, columns)}
        onChangePage={(page) => {
          const dealerFilters = configureFilterQuery()
          const searchParam = search ? `&q=${search}` : ''
          const billingFilterString = buildBillingFilterString()
          handlePagination(
            page,
            currentPage,
            perPage,
            totalRows,
            pages,
            setCurrentPage,
            getUpdatedDealersMetrics,
            `${DEALER_BASE_URL}&limit=`,
            `order_by=${sortedColumn}${dealerFilters ? `&${dealerFilters}` : ''}${
              billingFilterString ? `&${billingFilterString}` : ''
            }${searchParam}`,
          )
        }}
        onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
        onSort={(column, direction) => {
          const d = direction === 'asc' ? '' : '-'
          setSortedColumn(`${d}${column.sortBy}`)
        }}
        pagination
        paginationServer
        paginationDefaultPage={currentPage}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[5, 10, 20, 30, 50]}
        paginationTotalRows={totalRows}
        progressPending={loading}
        sortServer
      />

      {/* Export Modal */}
      <Modal
        open={showExportModal}
        title="Export Billing Cycle"
        description="Export a report for the given billing period."
        content={
          <div className="my-2 flex h-[300px] items-center">
            <BillingPeriodPicker
              periods={billingPeriods}
              selected={selectedPeriod}
              onChange={setSelectedPeriod}
            />
          </div>
        }
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => {
              setShowExportModal(false)
              setSelectedPeriod({})
            },
          },
          {
            type: 'submit',
            label: 'Export',
            disabled: !selectedPeriod,
            onClick: handleExport,
          },
        ]}
      />
    </div>
  )
}

Dealers.propTypes = {
  currentMonth: PropTypes.string,
  previousMonth: PropTypes.string,
  onFiltersChange: PropTypes.func,
}

export default Dealers
